import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, MessageBar, MessageBarBody } from '@fluentui/react-components';
import { NOT_READY_UPLOAD_WORDING, uploadLinkRequestedSchema } from '@vigeo/shared';
import { fixtureMode } from '../../lib/data-mode.js';

/** POST the one click. The instance id is opaque; nothing about the person travels in the URL. */
async function requestUploadLink(personId: string, instanceId: string): Promise<void> {
  if (fixtureMode) throw new Error('Upload links need the local test API; design fixtures are read-only.');
  const response = await fetch(`/api/people/${encodeURIComponent(personId)}/steps/${encodeURIComponent(instanceId)}/upload-link`, { method: 'POST' });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(typeof body === 'object' && body !== null && 'error' in body && typeof body.error === 'string' ? body.error : 'The upload link could not be requested.');
  }
  uploadLinkRequestedSchema.parse(body);
}

export interface RequestUploadLinkProps {
  readonly personId: string;
  readonly instanceId: string;
  /** Only someone who may issue steps gets the button; everyone sees the status. */
  readonly canRequest: boolean;
}

/**
 * A document the person said was not ready (#232): the status, and HR's one click.
 *
 * The click sends the step itself back to the person with a secure upload link (the email parks
 * until #61's separate-code workflow is live), so the file they later upload closes THIS
 * requirement through the normal review. A one-off request could not: it satisfies nothing.
 */
export function RequestUploadLink({ personId, instanceId, canRequest }: RequestUploadLinkProps) {
  const client = useQueryClient();
  const request = useMutation({
    mutationFn: () => requestUploadLink(personId, instanceId),
    onSuccess: async () => {
      // The row is derived from the step, so it is refetched with it.
      await Promise.all([
        client.invalidateQueries({ queryKey: ['checklist', personId] }),
        client.invalidateQueries({ queryKey: ['timeline', personId] }),
      ]);
    },
  });

  return (
    <>
      <Badge appearance="outline" size="small">{NOT_READY_UPLOAD_WORDING.hrStatus}</Badge>
      {canRequest ? (
        <Button size="small" disabled={request.isPending} onClick={() => request.mutate()}>
          {request.isPending ? 'Sending…' : NOT_READY_UPLOAD_WORDING.hrAction}
        </Button>
      ) : null}
      {request.isError ? (
        <MessageBar intent="error">
          <MessageBarBody>{request.error.message}</MessageBarBody>
        </MessageBar>
      ) : null}
    </>
  );
}
